'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { LoginGateModal } from './LoginGateModal'

type RequireAuth = (next?: () => void) => void

interface GateContextValue {
  isLoggedIn: boolean
  requireAuth: RequireAuth
}

const GateContext = createContext<GateContextValue | null>(null)

export function GateProvider({
  isLoggedIn,
  children,
}: {
  isLoggedIn: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  const requireAuth = useCallback<RequireAuth>(
    (next) => {
      if (isLoggedIn) {
        next?.()
        return
      }
      setOpen(true)
    },
    [isLoggedIn],
  )

  const value = useMemo<GateContextValue>(
    () => ({ isLoggedIn, requireAuth }),
    [isLoggedIn, requireAuth],
  )

  return (
    <GateContext.Provider value={value}>
      {children}
      <LoginGateModal open={open} onClose={() => setOpen(false)} />
    </GateContext.Provider>
  )
}

export function useGate(): GateContextValue {
  const ctx = useContext(GateContext)
  if (!ctx) {
    throw new Error('useGate must be used inside <GateProvider>')
  }
  return ctx
}
